import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import { CustomTypography } from "../custom-typography/custom-typography";
import { CancelIcon, DownloadIcon } from "../../assets/icon";
import {
  AssessmentHistoryDetail,
  AssessmentHistoryItem,
  getAssessmentHistory,
  getAssessmentHistoryDetail,
  getAssessmentReport,
} from "../../axios/quiz";
import useUserStore from "../../stores/userStore";
import { formatAssessmentScore, getAssessmentBaseScore } from "../../utils/assessmentScore";
import { AssessmentHistoryDetailView } from "./assessment-history-detail-view";
import {
  contentStyles,
  fullWidthGridItemStyles,
  loadingGridItemStyles,
  headerStyles,
  itemMetaStyles,
  itemStyles,
  itemTitleStyles,
  itemsGridStyles,
  modalBoxStyles,
  teamStatisticsButtonStyles,
} from "./styles";

interface AssessmentHistoryModalProps {
  open: boolean;
  onClose: () => void;
}

const formatFinishedAt = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatScore = (
  earned: number,
  max: number,
  bonusScore: number = 0,
): string => {
  const scoreLabel = formatAssessmentScore(earned, max, bonusScore);
  if (max > 0) {
    const percent = Math.round(
      (getAssessmentBaseScore(earned, bonusScore) / max) * 100,
    );
    return `${scoreLabel} (${percent}%)`;
  }
  return scoreLabel;
};

const formatListScore = (item: AssessmentHistoryItem): React.ReactNode => {
  if (
    typeof item.user_score === "number" ||
    typeof item.mandatory_score === "number"
  ) {
    return (
      <Box component="span" sx={{ display: "inline-flex", flexDirection: "column", gap: 0.25 }}>
        <Typography component="span" sx={itemMetaStyles}>
          Mandatory: {item.mandatory_score ?? 0} / {item.mandatory_max_score ?? 0}
        </Typography>
        <Typography component="span" sx={itemMetaStyles}>
          Bonus questions: {item.bonus_question_score ?? 0} /{" "}
          {item.bonus_question_max_score ?? 0}
        </Typography>
        <Typography component="span" sx={itemMetaStyles}>
          Total user score: {item.user_score ?? item.score_earned}
        </Typography>
        <Typography component="span" sx={itemMetaStyles}>
          Speed/streak bonus: +
          {item.gamification_bonus_score ?? item.bonus_score ?? 0}
        </Typography>
      </Box>
    );
  }

  return formatScore(item.score_earned, item.score_max, item.bonus_score);
};

const downloadBase64Xlsx = (base64: string, filename: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "Statistics.xlsx";
  link.click();

  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 5000);
};

export const AssessmentHistoryModal: React.FC<AssessmentHistoryModalProps> = ({
  open,
  onClose,
}) => {
  const isManager = useUserStore((state) => Boolean(state.user?.is_manager));
  const getFocusDisplayName = useUserStore((state) => state.getFocusDisplayName);
  const [items, setItems] = useState<AssessmentHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    string | null
  >(null);
  const [detail, setDetail] = useState<AssessmentHistoryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [downloadingAssessmentId, setDownloadingAssessmentId] = useState<
    string | null
  >(null);

  const resetDetailState = useCallback(() => {
    setSelectedAssessmentId(null);
    setDetail(null);
    setDetailError(null);
    setDetailLoading(false);
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAssessmentHistory();
      setItems(response.items ?? []);
    } catch {
      setError("Failed to load contests history.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (assessmentId: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      const response = await getAssessmentHistoryDetail(assessmentId);
      setDetail(response);
    } catch {
      setDetailError("Failed to load contest review.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchHistory();
    } else {
      resetDetailState();
      setDownloadingAssessmentId(null);
    }
  }, [open, fetchHistory, resetDetailState]);

  const handleClose = () => {
    resetDetailState();
    onClose();
  };

  const handleSelectItem = (assessmentId: string) => {
    setSelectedAssessmentId(assessmentId);
    fetchDetail(assessmentId);
  };

  const handleBackToList = () => {
    resetDetailState();
  };

  const handleTeamStatisticsDownload = async (
    event: React.MouseEvent,
    assessmentId: string,
  ) => {
    event.stopPropagation();
    if (downloadingAssessmentId) {
      return;
    }

    setDownloadingAssessmentId(assessmentId);
    try {
      const res = await getAssessmentReport(assessmentId);
      if (!res?.xlsx) {
        throw new Error("Empty XLSX data");
      }

      const filename = res.filename || "Statistics.xlsx";
      downloadBase64Xlsx(res.xlsx, filename);
      toast.success(`"${filename}" downloaded successfully!`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (downloadError) {
      console.error("Error downloading assessment report:", downloadError);
      toast.error("Failed to download team statistics. Please try again.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setDownloadingAssessmentId(null);
    }
  };

  const isDetailView = Boolean(selectedAssessmentId);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalBoxStyles}>
        <Box sx={headerStyles}>
          <CustomTypography fontSize={20} fontWeight={600} color="#FFFFFF">
            {isDetailView ? "Contest review" : "Contests history"}
          </CustomTypography>
          <CancelIcon
            color="#FFFFFF"
            onClick={handleClose}
            style={{ cursor: "pointer" }}
          />
        </Box>
        <Box sx={contentStyles}>
          {isDetailView ? (
            <AssessmentHistoryDetailView
              detail={detail}
              loading={detailLoading}
              error={detailError}
              onBack={handleBackToList}
              formatFinishedAt={formatFinishedAt}
              formatScore={formatScore}
            />
          ) : (
            <Box sx={itemsGridStyles}>
              {loading && (
                <Box sx={loadingGridItemStyles}>
                  <CircularProgress size={28} />
                </Box>
              )}
              {!loading && error && (
                <Box sx={fullWidthGridItemStyles}>
                  <Typography sx={itemMetaStyles}>{error}</Typography>
                </Box>
              )}
              {!loading && !error && items.length === 0 && (
                <Box sx={fullWidthGridItemStyles}>
                  <Typography sx={itemMetaStyles}>
                    No completed contests yet.
                  </Typography>
                </Box>
              )}
              {!loading &&
                !error &&
                items.map((item) => {
                  const isDownloading =
                    downloadingAssessmentId === item.assessment_id;

                  return (
                    <Box
                      key={item.assessment_id}
                      sx={itemStyles}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectItem(item.assessment_id)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          handleSelectItem(item.assessment_id);
                        }
                      }}
                    >
                      <Typography sx={itemTitleStyles}>
                        {item.title || "Contest"}
                      </Typography>
                      {item.focus ? (
                        <Typography sx={itemMetaStyles}>
                          {getFocusDisplayName(item.focus)}
                        </Typography>
                      ) : null}
                      <Box sx={itemMetaStyles}>
                        {formatFinishedAt(item.finished_at)}
                        {typeof item.user_score === "number" ||
                        typeof item.mandatory_score === "number" ? (
                          <Box sx={{ mt: 0.5 }}>{formatListScore(item)}</Box>
                        ) : (
                          <> · {formatListScore(item)}</>
                        )}
                      </Box>
                      {isManager ? (
                        <Button
                          variant="contained"
                          sx={teamStatisticsButtonStyles}
                          disabled={Boolean(downloadingAssessmentId)}
                          onClick={(event) =>
                            handleTeamStatisticsDownload(
                              event,
                              item.assessment_id,
                            )
                          }
                        >
                          {isDownloading ? (
                            <CircularProgress size={14} sx={{ color: "#fff" }} />
                          ) : (
                            <DownloadIcon
                              style={{ strokeWidth: 3, fontSize: "14px" }}
                            />
                          )}
                          <span>Team Statistics</span>
                        </Button>
                      ) : null}
                    </Box>
                  );
                })}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
